import React from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import Reveal from "../../../../components/LandingPage/v2/Reveal";
import LocationSvg from "/public/landings/assets/icons/business-division/location";
import {
	SectionV2,
	InnerV2,
	HeadV2,
	GlassCard,
} from "../../../../components/LandingPage/v2/primitives";
import playWarpTransition from "../../../../components/LandingPage/v2/warpTransition";
import DIVISION_THEMES from "../../../../data/businessDivisionThemes";

/** the division route, in journey order — mirrors the old timeline */
const STEPS = Object.entries(DIVISION_THEMES).map(([id, cfg]) => ({
	id,
	accent: cfg.accent,
}));

/**
 * Replaces the old timeline image: the visitor's position in the journey as
 * three connected stops; the other divisions stay one click away.
 */
const Journey = ({ divisionId, theme }) => {
	const { t } = useTranslation();
	const router = useRouter();

	// real hrefs for crawlers; hopping between divisions keeps the same
	// cinematic language as the home
	const stepHref = (step) =>
		`/${router.query.locale}/business-division/${step.id}/`;

	const openStep = (step, event) => {
		event?.preventDefault();
		const rect = event?.currentTarget?.getBoundingClientRect?.();
		playWarpTransition({
			x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
			y: rect ? rect.top + rect.height / 2 : window.innerHeight / 2,
			accent: step.accent,
			navigate: () => router.push(stepHref(step)),
		});
	};

	const title = t("businessDivisionV2.journeyTitle");
	const titleWords = title.split(" ");
	const lastWord = titleWords.pop();

	return (
		<SectionV2 $accent={theme.accent} $glowAt="20% 0%">
			<InnerV2>
				<Reveal>
					<HeadV2 $accent={theme.accent}>
						<span className="overline">
							{t("businessDivisionV2.journeyOverline")}
						</span>
						<h2>
							{titleWords.join(" ")} <em>{lastWord}</em>
						</h2>
						<p>{t(`businessDivision.${divisionId}Description`)}</p>
					</HeadV2>
				</Reveal>

				<Reveal y={34} delay={120}>
					<Rail aria-hidden="true">
						{STEPS.map((step, i) => (
							<span
								key={step.id}
								className={[
									"cell",
									step.id === divisionId ? "active" : "",
									i === 0 ? "first" : "",
									i === STEPS.length - 1 ? "last" : "",
								]
									.filter(Boolean)
									.join(" ")}
								style={{ color: step.accent }}
							>
								{step.id === divisionId && <LocationSvg className="pin" />}
								<i className="dot" />
							</span>
						))}
					</Rail>
					<Track>
						{STEPS.map((step, i) => {
							const isHere = step.id === divisionId;
							return (
								<Stop
									key={step.id}
									$accent={step.accent}
									$active={isHere}
									as={isHere ? "div" : "a"}
									href={isHere ? undefined : stepHref(step)}
									onClick={
										isHere ? undefined : (event) => openStep(step, event)
									}
								>
									<span className="num">{String(i + 1).padStart(2, "0")}</span>
									<h3>{t(`businessDivision.${step.id}`)}</h3>
									<p>{t(`businessDivision.${step.id}Hook`)}</p>
									{isHere ? (
										<span className="badge">
											{t("businessDivisionV2.youAreHere")}
										</span>
									) : (
										<span className="go">
											{t("businessDivisionV2.explore")}
											<svg
												width="13"
												height="13"
												viewBox="0 0 16 16"
												fill="none"
												aria-hidden="true"
											>
												<path
													d="M3 8h10M9 4l4 4-4 4"
													stroke="currentColor"
													strokeWidth="1.6"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</span>
									)}
								</Stop>
							);
						})}
					</Track>
				</Reveal>

				<Reveal delay={200}>
					<Closing>{t(`businessDivision.${divisionId}Description2`)}</Closing>
				</Reveal>
			</InnerV2>
		</SectionV2>
	);
};

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; opacity: 0.9; }
  70% { box-shadow: 0 0 0 12px transparent; opacity: 1; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 0.9; }
`;

/* the pin hovers like a live GPS marker settling on its spot */
const bob = keyframes`
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-5px); }
`;

/**
 * The timeline lives in its own row above the cards: same grid template, so
 * each node sits exactly over its card. Every cell draws two half-segments
 * that overshoot half the grid gap, meeting in the middle so the line reads
 * continuous without ever crossing a card.
 */
const Rail = styled.div`
  display: none;

  @media (min-width: 992px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
    margin-bottom: 22px;
  }

  .cell {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 13px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      height: 1px;
      background: currentColor;
      opacity: 0.3;
    }

    &::before {
      left: -14px;
      right: calc(50% + 13px);
    }

    &::after {
      left: calc(50% + 13px);
      right: -14px;
    }

    &.first::before,
    &.last::after {
      content: none;
    }
  }

  .dot {
    /* globals.scss "* { color }" hits the <i> directly and would flip
       currentColor to dark purple — pull the cell's accent back in */
    color: inherit;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1.5px solid currentColor;
    background: transparent;
  }

  /* the visitor's position is a GPS pin dropped on the track; the dot under
     its tip shrinks into the pulsing "landing" ring */
  .pin {
    position: absolute;
    left: 50%;
    bottom: 50%;
    width: 30px;
    height: 30px;
    /* hover clear of the landing ring — a pin touching the dot reads glued */
    margin-bottom: 7px;
    transform: translateX(-50%);
    color: inherit;
    filter: drop-shadow(0 4px 12px currentColor);
    animation: ${bob} 2.4s ease-in-out infinite;

    /* the artwork ships with a hardcoded green fill — retint to the accent;
       the path needs its own color: inherit or the global "*" rule would
       resolve currentColor to dark purple */
    path {
      color: inherit;
      fill: currentColor;
    }
  }

  .cell.active .dot {
    width: 8px;
    height: 8px;
    border: none;
    background: currentColor;
    animation: ${pulse} 2.4s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .cell.active .dot,
    .pin {
      animation: none;
    }
  }
`;

const Track = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 28px;
  }
`;

const Stop = styled(GlassCard)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 28px 28px 26px;
  text-align: left;
  /* inactive stops render as <a> */
  text-decoration: none;
  cursor: ${(p) => (p.$active ? "default" : "pointer")};
  border-color: ${(p) =>
		p.$active ? p.$accent + "59" : "rgba(255, 255, 255, 0.08)"};
  box-shadow: ${(p) =>
		p.$active ? `0 24px 64px -28px ${p.$accent + "4d"}` : "none"};

  .num {
    font-family: "Hind Madurai";
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 2px;
    color: ${(p) => (p.$active ? p.$accent : "#5c6470")};
    margin-bottom: 14px;
  }

  h3 {
    font-family: "Hind Madurai";
    font-size: 1.35rem;
    font-weight: 600;
    text-transform: capitalize;
    color: ${(p) => (p.$active ? p.$accent : "#ffffff")};
    text-shadow: ${(p) => (p.$active ? `0 0 26px ${p.$accent + "59"}` : "none")};
    margin: 0 0 8px 0;
  }

  p {
    font-family: "Hind Madurai";
    font-size: 0.95rem;
    line-height: 1.6;
    color: #a9afb8;
    margin: 0 0 18px 0;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    font-family: "Hind Madurai";
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2.4px;
    text-transform: uppercase;
    color: #000104;
    background: ${(p) => p.$accent};
    border-radius: 100px;
    padding: 6px 14px;
    box-shadow: 0 0 26px ${(p) => p.$accent + "66"};
  }

  .go {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: "Hind Madurai";
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: ${(p) => p.$accent};
    transition: gap 0.25s ease;

    /* globals.scss forces "* { color }" down the svg subtree */
    svg,
    svg * {
      color: inherit;
    }
  }

  &:hover .go {
    gap: 13px;
  }

`;

const Closing = styled.p`
  font-family: "Hind Madurai";
  font-size: clamp(0.95rem, 1.25vw, 1.1rem);
  line-height: 1.75;
  color: #a9afb8;
  text-align: center;
  max-width: 760px;
  margin: clamp(36px, 5vw, 56px) auto 0;
`;

export default Journey;
